import clsx from 'clsx';
// mantine
import { Anchor, Pill, UnstyledButton } from '@mantine/core';
// icons
import { Trash2, Edit } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import type {
    TIncomingWebhook,
    TIncomingWebhookUpdateData
} from '@newo/api/gateway';
import IncomingWebhookForm from '@/pages/Integrations/components/IncomingWebhooks/components/IncomingWebhookForm';
// utils
import { useIncomingWebhookDeleteMutation } from '@/services/webhooks/webhooks.queries.ts';
// assets
import styles from '@/pages/Integrations/components/IncomingWebhooks/components/WebhookItem/WebhookItem.module.css';

interface WebhookItemProps {
    webhook: TIncomingWebhook;
    onUpdate: (data: TIncomingWebhookUpdateData) => void;
    onDelete: () => void;
}

const WebhookItem = ({ webhook, onDelete, onUpdate }: WebhookItemProps) => {
    const {
        mutate: deleteWebhook,
        isPending,
        isSuccess
    } = useIncomingWebhookDeleteMutation();

    const handleDelete = () => {
        deleteWebhook(
            { webhookId: webhook.id },
            {
                onSuccess() {
                    onDelete();
                }
            }
        );
    };

    return (
        <article
            className={clsx(styles.item, {
                [styles.isPending]: isPending || isSuccess
            })}
        >
            <div className={styles.actions}>
                <PopoverButton trigger={<Edit />}>
                    {(close) => (
                        <IncomingWebhookForm
                            connectorIdn={webhook.connector_idn}
                            webhook={webhook}
                            onClose={close}
                            onSubmit={onUpdate}
                        />
                    )}
                </PopoverButton>
                <UnstyledButton className="text-red-6" onClick={handleDelete}>
                    <Trash2 />
                </UnstyledButton>
            </div>
            <div className={styles.label}>Idn</div>
            <div>{webhook.idn}</div>
            <div className={styles.label}>url</div>
            <div className="break-all">
                <Anchor href={webhook.url} size="xs" target="_blank">
                    {webhook.url}
                </Anchor>
            </div>
            {webhook.description ? (
                <>
                    <div className={styles.label}>description</div>
                    <div>{webhook.description}</div>
                </>
            ) : undefined}
            <div className={styles.label}>events</div>
            <div className="flex gap-2">
                {webhook.event_idns.map((eventIdn) => (
                    <Pill key={eventIdn}>{eventIdn}</Pill>
                ))}
            </div>
            <div className={styles.label}>allowed IPs</div>
            <div className="flex gap-2">
                {webhook.allowed_ips.map((ip) => (
                    <Pill key={ip}>{ip}</Pill>
                ))}
            </div>
        </article>
    );
};

export default WebhookItem;
